// src/pages/admin/SuperAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { companyService, userService, logService, subscriptionRequestService } from '../../services/apiServices';
import { dashboardService } from '../../services/dashboardServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  ActivityChart,
  UserDistributionChart,
  CompanyStatusChart,
  MetricsCard
} from '../../components/dashboard/DashboardCharts';
import {
  UserEngagementChart
} from '../../components/dashboard/EnhancedCharts';
import LayoutSettingsModal from '../../components/dashboard/LayoutSettingsModal';
import PendingRequestsNotification from '../../components/admin/PendingRequestsNotification';

// Icons for metric cards
const ICONS = {
  company: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
  user: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  analysis: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
};

const SuperAdminDashboard = () => {
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalCompanies: 0,
      totalUsers: 0,
      totalAnalyses: 0
    },
    historicalMetrics: {
      totalCompanies: 0,
      totalUsers: 0,
      totalAnalyses: 0
    },
    recentActivities: [],
    userDistribution: [],
    companyStatuses: [],
    activityTrends: [],
    engagementData: []
  });

  // State for widget layout
  const [widgetLayout, setWidgetLayout] = useState({
    metrics: { visible: true, position: 0 },
    activityChart: { visible: true, position: 1 },
    userChart: { visible: true, position: 2 },
    engagementChart: { visible: true, position: 3 },
    activities: { visible: true, position: 4 },
    companyChart: { visible: true, position: 5 }
  });

  // State for layout settings modal
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch metrics data using the improved dashboard service
      const response = await dashboardService.getSuperAdminMetrics();
      let metricsData;

      if (response.success) {
        metricsData = response.data;
      } else {
        throw new Error('Failed to fetch dashboard metrics');
      }

      // Fetch historical data for trend calculation
      let historicalData = {};
      try {
        const historicalResponse = await dashboardService.getSuperAdminHistoricalMetrics(7);
        if (historicalResponse.success) {
          historicalData = historicalResponse.data.previousPeriod || {};
        }
      } catch (err) {
        console.warn('Historical data not available');
        historicalData = {};
      }

      // Prepare user distribution data for charts
      const userDistribution = [
        { name: 'Super Admins', value: metricsData.usersByRole?.superadmin || 0 },
        { name: 'Admins', value: metricsData.usersByRole?.admin || 0 },
        { name: 'Analystes', value: metricsData.usersByRole?.simpleuser || 0 }
      ];

      // Prepare company status data for charts
      const companyStatuses = [
        { name: 'Actives', count: metricsData.companyStatuses?.active || 0 },
        { name: 'Inactives', count: metricsData.companyStatuses?.inactive || 0 }
      ];

      // Use real activity trends data from API
      const activityTrends = metricsData.activityTrends || [];

      // Use real user engagement data from API
      const engagementData = metricsData.userEngagement || [];

      // Fetch pending subscription requests separately to ensure fresh data
      let pendingRequests = [];
      try {
        const requestsResponse = await subscriptionRequestService.getAllRequests();
        if (requestsResponse.success) {
          // Filter only pending requests
          pendingRequests = (requestsResponse.data || []).filter(req => req.status === 'pending');
        }
      } catch (err) {
        console.warn('Could not fetch subscription requests:', err);
      }

      setDashboardData({
        metrics: {
          totalCompanies: metricsData.totalCompanies || 0,
          totalUsers: metricsData.totalUsers || 0,
          totalAnalyses: metricsData.totalAnalyses || metricsData.activeAnalyses || 0
        },
        historicalMetrics: {
          totalCompanies: historicalData.totalCompanies || 0,
          totalUsers: historicalData.totalUsers || 0,
          totalAnalyses: historicalData.totalAnalyses || historicalData.activeAnalyses || 0
        },
        recentActivities: metricsData.recentActivities || [],
        pendingSubscriptionRequests: pendingRequests,
        userDistribution,
        companyStatuses,
        activityTrends,
        engagementData
      });
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      setError('Erreur lors du chargement des données. Veuillez réessayer plus tard.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch dashboard data on component mount and when returning to page
  useEffect(() => {
    fetchDashboardData();

    // Refresh data when window regains focus (user returns from another tab/page)
    const handleFocus = () => {
      fetchDashboardData();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Helper function to determine activity badge color
  const getActivityBadgeColor = (actionType) => {
    if (actionType.includes('LOGIN')) return 'bg-blue-100 text-blue-800';
    if (actionType.includes('CREATE')) return 'bg-green-100 text-green-800';
    if (actionType.includes('UPDATE')) return 'bg-yellow-100 text-yellow-800';
    if (actionType.includes('DELETE')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error message if something went wrong
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Erreur</p>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 bg-red-100 px-3 py-1 rounded-md hover:bg-red-200"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const { metrics, historicalMetrics, recentActivities, pendingSubscriptionRequests, userDistribution, companyStatuses, activityTrends, engagementData } = dashboardData;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Super Administrateur</h1>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            <span>Dernière mise à jour : {new Date().toLocaleString()}</span>
          </div>
          <button
            onClick={fetchDashboardData}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            title="Actualiser les données"
            disabled={isLoading}
          >
            <svg className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={() => setShowLayoutSettings(true)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            title="Personnaliser le tableau de bord"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Layout Settings Modal */}
      {showLayoutSettings && (
        <LayoutSettingsModal
          widgetLayout={widgetLayout}
          setWidgetLayout={setWidgetLayout}
          onClose={() => setShowLayoutSettings(false)}
        />
      )}

      {/* Pending User Limit Requests Notification */}
      <PendingRequestsNotification />

      {/* Main metrics cards */}
      {widgetLayout.metrics.visible && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <MetricsCard
            title="Entreprises"
            value={metrics.totalCompanies}
            previousValue={historicalMetrics.totalCompanies}
            icon={ICONS.company}
            color="blue"
            linkText="Gérer les entreprises"
            linkUrl="/superadmin/companies"
            trendPeriod="vs. semaine précédente"
          />

          <MetricsCard
            title="Utilisateurs"
            value={metrics.totalUsers}
            previousValue={historicalMetrics.totalUsers}
            icon={ICONS.user}
            color="green"
            linkText="Gérer les utilisateurs"
            linkUrl="/superadmin/users"
            trendPeriod="vs. semaine précédente"
          />

          <MetricsCard
            title="Analyses"
            value={metrics.totalAnalyses}
            previousValue={historicalMetrics.totalAnalyses}
            icon={ICONS.analysis}
            color="purple"
            linkText="Voir toutes les analyses"
            linkUrl="/superadmin/analyses"
            trendPeriod="vs. semaine précédente"
          />
        </div>
      )}

      {/* Subscription Requests Notifications */}
      {pendingSubscriptionRequests.length > 0 && (
        <div className="bg-white border-l-4 border-orange-400 p-6 rounded-lg shadow-md mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-orange-800">
                  Demandes d'Abonnement en Attente
                </h3>
                <p className="text-sm text-orange-700">
                  {pendingSubscriptionRequests.length} demande(s) nécessite(nt) votre attention
                </p>
              </div>
            </div>
            <Link
              to="/superadmin/subscriptions"
              className="bg-orange-100 text-orange-800 px-4 py-2 rounded-md hover:bg-orange-200 transition-colors text-sm font-medium"
            >
              Gérer toutes les demandes
            </Link>
          </div>

          <div className="space-y-3">
            {pendingSubscriptionRequests.map((request) => (
              <div key={request._id} className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-gray-900">{request.companyId?.name || request.companyName}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${request.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          request.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            request.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                        }`}>
                        {request.priority === 'urgent' ? 'Urgent' :
                          request.priority === 'high' ? 'Élevée' :
                            request.priority === 'medium' ? 'Moyenne' : 'Faible'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <span className="capitalize">{request.requestType}</span> - {request.requestedDuration} {request.durationType}
                    </p>
                    <p className="text-xs text-gray-500">
                      Demandé par {request.requestedByName} • {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                    {request.message && (
                      <p className="text-sm text-gray-700 mt-1 italic">"{request.message}"</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Link
                      to={`/superadmin/subscriptions?requestId=${request._id}`}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Examiner
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {widgetLayout.activityChart.visible && (
          <ActivityChart data={activityTrends} />
        )}
        {widgetLayout.userChart.visible && (
          <UserDistributionChart data={userDistribution} />
        )}
      </div>

      {/* Enhanced charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {widgetLayout.engagementChart.visible && (
          <UserEngagementChart data={engagementData} />
        )}
      </div>

      {/* Recent Activities Card */}
      {widgetLayout.activities.visible && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-8 hover:shadow-lg transition-shadow duration-300">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Activités récentes</h2>
            <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
              Dernières {recentActivities.length} activités
            </span>
          </div>

          {recentActivities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucune activité récente à afficher
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentActivities.map((activity) => (
                    <tr key={activity.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActivityBadgeColor(activity.actionType)}`}>
                          {activity.actionType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {activity.userName || activity.userId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {activity.companyId?.name || activity.companyName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 text-right">
            <Link
              to="/admin/activities"
              className="text-blue-600 hover:text-blue-800 inline-flex items-center font-medium transition-colors duration-200"
            >
              Voir tous les logs d'activité
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      )}

      {/* Company Status Chart */}
      {widgetLayout.companyChart.visible && (
        <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">Statut des Entreprises</h2>
          <CompanyStatusChart data={companyStatuses} />
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;